import { useCallback, useRef } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { Printer, LayoutGrid, FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { escapeHtml, buildPublicUrl } from '@/components/RoomQRCode'
import type { Room } from '@/types'

type PrintLayout = 'grid' | 'one-per-page'

interface BulkQRPrintProps {
  rooms: Room[]
  buildingName: string
}

/**
 * Renders a hidden container of QR code SVGs and provides a dropdown button
 * to print all QR codes for rooms in a building.
 *
 * Two layout options:
 * - grid: multiple QR codes per page (3 columns)
 * - one-per-page: each QR code on its own page
 */
export function BulkQRPrint({ rooms, buildingName }: BulkQRPrintProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  const handlePrint = useCallback(
    (selectedLayout: PrintLayout) => {
      if (rooms.length === 0) return

      const printWindow = window.open('', '_blank')
      if (!printWindow) return

      const safeBuildingName = escapeHtml(buildingName)

      // Serialize QR code SVGs from the hidden DOM container
      const qrContainer = containerRef.current
      if (!qrContainer) return

      const svgMap = new Map<string, string>()
      qrContainer.querySelectorAll('[data-room-id]').forEach((el) => {
        const roomId = el.getAttribute('data-room-id')
        const svg = el.querySelector('svg')
        if (roomId && svg) {
          svgMap.set(roomId, new XMLSerializer().serializeToString(svg))
        }
      })

      const isGrid = selectedLayout === 'grid'

      const qrHtml = rooms
        .map((room) => {
          const svgStr = svgMap.get(room.id) || ''
          const url = buildPublicUrl(room.id)
          return `
            <div class="qr-item">
              <div class="qr-svg">${svgStr}</div>
              <div class="room-name">${escapeHtml(room.name)}</div>
              <div class="building-name">${safeBuildingName}</div>
              <div class="url">${escapeHtml(url)}</div>
            </div>
          `
        })
        .join('')

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>QR Codes - ${safeBuildingName}</title>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body {
                font-family: system-ui, sans-serif;
                padding: 0.5in;
              }
              h1 {
                font-size: 18px;
                text-align: center;
                margin-bottom: 24px;
              }
              ${
                isGrid
                  ? `
              .qr-grid {
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                gap: 24px;
              }
              .qr-item {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 16px 8px;
                page-break-inside: avoid;
              }
              .qr-svg svg { width: 140px; height: 140px; }
              .room-name {
                font-size: 14px;
                font-weight: bold;
                margin-top: 8px;
                text-align: center;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                max-width: 100%;
              }
              .building-name {
                font-size: 11px;
                color: #666;
                margin-top: 2px;
                text-align: center;
              }
              .url {
                font-size: 7px;
                color: #aaa;
                margin-top: 6px;
                word-break: break-all;
                text-align: center;
              }
              `
                  : `
              .qr-grid {
                display: block;
              }
              .qr-item {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                min-height: calc(100vh - 1in);
                padding: 1in 0;
                page-break-after: always;
              }
              .qr-item:last-child {
                page-break-after: avoid;
              }
              .qr-svg svg { width: 250px; height: 250px; }
              .room-name {
                font-size: 32px;
                font-weight: bold;
                margin-top: 20px;
                text-align: center;
              }
              .building-name {
                font-size: 18px;
                color: #666;
                margin-top: 6px;
                text-align: center;
              }
              .url {
                font-size: 9px;
                color: #aaa;
                margin-top: 16px;
                word-break: break-all;
                text-align: center;
              }
              `
              }
              @media print {
                @page { margin: 0.5in; }
                body { padding: 0; }
                h1 { margin-bottom: 16px; }
              }
            </style>
          </head>
          <body>
            <h1>QR Codes - ${safeBuildingName}</h1>
            <div class="qr-grid">
              ${qrHtml}
            </div>
            <script>window.onload = function() { window.onafterprint = function() { window.close(); }; window.print(); }</script>
          </body>
        </html>
      `)
      printWindow.document.close()
    },
    [rooms, buildingName]
  )

  if (rooms.length === 0) return null

  return (
    <>
      {/* Hidden container with QR code SVGs for serialization during print */}
      <div ref={containerRef} className="hidden" aria-hidden="true">
        {rooms.map((room) => (
          <div key={room.id} data-room-id={room.id}>
            <QRCodeSVG
              value={buildPublicUrl(room.id)}
              size={250}
              level="M"
              includeMargin={false}
            />
          </div>
        ))}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            <Printer className="mr-2 h-4 w-4" />
            Print All QR Codes
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => handlePrint('grid')}>
            <LayoutGrid className="mr-2 h-4 w-4" />
            Grid Layout (multiple per page)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handlePrint('one-per-page')}>
            <FileText className="mr-2 h-4 w-4" />
            One Per Page
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}
